import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { useMutation, useQuery, useQueryClient, type QueryFilters } from "@tanstack/react-query";
import { notificationApiService } from "../services/notificationApiService";
import {
  fromNotificationDtoToNotification,
  type Notification,
} from "../types/notificationTypes";

const staleTime = 1 * 60 * 1000; // 1 minute (notifications should be fresher)

// Query keys
export const notificationKeys = {
  all: ["notification"] as const,
  list: (businessId: string, unreadOnly?: boolean, userId?: string) =>
    [...notificationKeys.all, "list", businessId, { unreadOnly, userId }] as const,
  unreadCount: (businessId: string, userId?: string) =>
    [...notificationKeys.all, "unreadCount", businessId, { userId }] as const,
};

interface NotificationMutationScope {
  businessId: string;
  userId: string;
  generation: number;
}

function useNotificationMutationScope() {
  const queryClient = useQueryClient();
  const { businessId } = useCurrentBusiness();
  const { user, getAuthGeneration } = useAuth();

  const captureScope = (): NotificationMutationScope => {
    if (!user || !businessId) throw new Error("Sign in and select a business before updating notifications.");
    return { businessId, userId: user.id, generation: getAuthGeneration() };
  };

  const invalidateScope = async (scope?: NotificationMutationScope) => {
    if (!scope || scope.generation !== getAuthGeneration()) return;
    const filters: QueryFilters = {
      queryKey: notificationKeys.all,
      predicate: (query) => query.queryKey[2] === scope.businessId
        && (query.queryKey[3] as { userId?: string })?.userId === scope.userId,
    };
    await queryClient.cancelQueries(filters);
    if (scope.generation !== getAuthGeneration()) return;
    // Refetch authoritative read state: notifications may have arrived after the operation began.
    await queryClient.invalidateQueries(filters);
  };

  return { captureScope, invalidateScope };
}

export const useNotifications = (unreadOnly = false) => {
  const { businessId } = useCurrentBusiness();
  const { user } = useAuth();

  return useQuery<Notification[], Error>({
    queryKey: notificationKeys.list(businessId || "", unreadOnly, user?.id),
    queryFn: async () => {
      const api = notificationApiService.forBusiness(businessId || "");
      const dtos = await api.getAll(unreadOnly);
      return dtos.map(fromNotificationDtoToNotification);
    },
    enabled: !!user && !!businessId,
    staleTime,
  });
};

export const useUnreadNotificationsCount = () => {
  const { businessId } = useCurrentBusiness();
  const { user } = useAuth();

  return useQuery<number, Error>({
    queryKey: notificationKeys.unreadCount(businessId || "", user?.id),
    queryFn: async () => {
      const api = notificationApiService.forBusiness(businessId || "");
      const dtos = await api.getAll(true);
      return dtos.length;
    },
    enabled: !!user && !!businessId,
    staleTime,
    refetchInterval: 30 * 1000, // Poll every 30 seconds for new notifications
  });
};

export const useMarkNotificationAsRead = () => {
  const { businessId } = useCurrentBusiness();
  const { captureScope, invalidateScope } = useNotificationMutationScope();

  return useMutation<Notification, Error, string, NotificationMutationScope>({
    onMutate: captureScope,
    mutationFn: async (id: string) => {
      const api = notificationApiService.forBusiness(businessId || "");
      const resultDto = await api.markAsRead(id);
      return fromNotificationDtoToNotification(resultDto);
    },
    onSuccess: (_result, _variables, scope) => invalidateScope(scope),
  });
};

export const useMarkAllNotificationsAsRead = () => {
  const { businessId } = useCurrentBusiness();
  const { captureScope, invalidateScope } = useNotificationMutationScope();

  return useMutation<{ message: string }, Error, void, NotificationMutationScope>({
    onMutate: captureScope,
    mutationFn: async () => {
      const api = notificationApiService.forBusiness(businessId || "");
      return api.markAllAsRead();
    },
    onSuccess: (_result, _variables, scope) => invalidateScope(scope),
  });
};

export const useDeleteNotification = () => {
  const { businessId } = useCurrentBusiness();
  const { captureScope, invalidateScope } = useNotificationMutationScope();

  return useMutation<{ message: string }, Error, string, NotificationMutationScope>({
    onMutate: captureScope,
    mutationFn: async (id: string) => {
      const api = notificationApiService.forBusiness(businessId || "");
      return api.delete(id);
    },
    onSuccess: (_result, _variables, scope) => invalidateScope(scope),
  });
};
